import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '@/contexts/LanguageContext';
import SEOHead from '@/components/common/SEOHead';
import { LoadingPage } from '@/components/common/Loading';
import { ChevronRight, Trophy, Calendar, Swords, Sparkles, MapPin } from 'lucide-react';
import WCInternalLinks from '@/components/worldcup/WCInternalLinks';
import { translateGroupName, translateCountryName, wcMatchSlug } from '@/lib/wcTranslations';
import WCSubmenu from '@/components/worldcup/WCSubmenu';

const API_BASE = '';
const LOCALE_MAP = { tr: 'tr-TR', en: 'en-US', es: 'es-ES', de: 'de-DE', fr: 'fr-FR', pt: 'pt-BR', it: 'it-IT', nl: 'nl-NL', ru: 'ru-RU', uk: 'uk-UA', ar: 'ar-SA', id: 'id-ID' };

function _teamSlug(name) {
  const map = {
    'Türkiye': 'turkiye', 'Brazil': 'brazil', 'France': 'france', 'Argentina': 'argentina',
    'England': 'england', 'Germany': 'germany', 'Spain': 'spain', 'Portugal': 'portugal',
    'Netherlands': 'netherlands', 'Croatia': 'croatia', 'Belgium': 'belgium', 'USA': 'usa',
    'Mexico': 'mexico', 'Canada': 'canada', 'Japan': 'japan', 'South Korea': 'south-korea',
    'Australia': 'australia', 'Morocco': 'morocco', 'Senegal': 'senegal', 'Uruguay': 'uruguay',
    'Colombia': 'colombia', 'Ecuador': 'ecuador', 'Paraguay': 'paraguay',
    'Switzerland': 'switzerland', 'Austria': 'austria', 'Sweden': 'sweden', 'Norway': 'norway',
    'Scotland': 'scotland', 'Iran': 'iran', 'Saudi Arabia': 'saudi-arabia', 'Qatar': 'qatar',
    'Egypt': 'egypt', 'Tunisia': 'tunisia', 'Algeria': 'algeria', 'Ivory Coast': 'ivory-coast',
    'Ghana': 'ghana', 'Congo DR': 'congo-dr', 'South Africa': 'south-africa',
    'Cape Verde Islands': 'cape-verde', 'New Zealand': 'new-zealand', 'Panama': 'panama',
    'Haiti': 'haiti', 'Bosnia & Herzegovina': 'bosnia', 'Czech Republic': 'czech-republic',
    'Jordan': 'jordan', 'Iraq': 'iraq', 'Uzbekistan': 'uzbekistan', 'Curaçao': 'curacao',
  };
  return map[name] || name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

export default function WorldCupTeam() {
  const { slug } = useParams();
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const locale = LOCALE_MAP[currentLang] || 'en-US';
  const wcBase = currentLang === 'tr' ? `/${currentLang}/dunya-kupasi-2026` : `/${currentLang}/world-cup-2026`;
  const teamPathSeg = currentLang === 'tr' ? 'takim' : 'team';
  const grpPathSeg = currentLang === 'tr' ? 'grup' : 'group';
  const matchPathSeg = currentLang === 'tr' ? 'mac' : 'match';

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_BASE}/api/worldcup/team/${slug}?lang=${currentLang}`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [slug, currentLang]);

  if (loading) return <LoadingPage />;
  if (!data?.team) return <div className="container mx-auto px-4 py-8 text-slate-400">{t('wc.teamNotFound')}</div>;

  const { team, group_standings, wc_matches, all_group_matches, opponents, seo } = data;
  const isTurkey = team.name === 'Türkiye';
  const groupLetterMatch = team.group.match(/Group\s*([A-L])/i);
  const groupLetter = groupLetterMatch ? groupLetterMatch[1].toLowerCase() : team.group.replace(/[^a-zA-Z]/g, '').toLowerCase();
  const translatedGroup = translateGroupName(team.group, currentLang);

  return (
    <div data-testid="worldcup-team">
      <WCSubmenu />
      <SEOHead title={seo?.title} description={seo?.description} canonical={seo?.canonical} />

      {/* Hero */}
      <div className="relative overflow-hidden" style={{
        background: isTurkey
          ? 'linear-gradient(135deg, #450a0a 0%, #7f1d1d 30%, #dc2626 60%, #450a0a 100%)'
          : 'linear-gradient(135deg, #020617 0%, #0a1628 30%, #1a472a 70%, #0a1628 100%)'
      }}>
        <div className="absolute top-0 right-0 w-48 h-48 opacity-10">
          <img src={team.logo} alt="" className="w-full h-full object-contain" />
        </div>
        <div className="relative z-10 container mx-auto px-4 max-w-7xl py-8 md:py-14">
          <div className="flex items-center gap-2 text-sm text-white/50 mb-4">
            <Link to={`/${currentLang}`} className="hover:text-white/80">{t('nav.home')}</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={wcBase} className="hover:text-white/80">{t('wc.breadcrumb')}</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{translateCountryName(team.name, currentLang)}</span>
          </div>
          <div className="flex items-center gap-5">
            <img src={team.logo} alt={team.name} className="w-20 h-20 md:w-24 md:h-24 drop-shadow-lg" />
            <div>
              <h1 className="text-4xl md:text-5xl font-black text-white uppercase" style={{ fontFamily: 'var(--font-heading)' }}>{translateCountryName(team.name, currentLang)}</h1>
              <p className="text-lg text-white/60 mt-1">{translatedGroup} - FIFA {t('wc.breadcrumb')}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-7xl py-8 space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">

            {/* Group Standings */}
            <section className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50 flex items-center justify-between">
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                  <Trophy className="w-4 h-4 inline mr-2 text-amber-400" />{translatedGroup} - {t('wc.standings')}
                </h2>
                <Link to={`${wcBase}/${grpPathSeg}/${groupLetter}`} className="text-[10px] text-amber-400 hover:text-amber-300">{t('wc.detail')}</Link>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-xs text-slate-500 border-b border-slate-800/30">
                  <th className="text-left px-4 py-2 w-8">#</th><th className="text-left px-4 py-2">{t('wc.team')}</th>
                  <th className="text-center px-2 py-2">{t('wc.p')}</th><th className="text-center px-2 py-2">{t('wc.w')}</th>
                  <th className="text-center px-2 py-2">{t('wc.d')}</th><th className="text-center px-2 py-2">{t('wc.l')}</th>
                  <th className="text-center px-2 py-2 font-bold">{t('wc.pts')}</th>
                </tr></thead>
                <tbody>
                  {group_standings.map((s, i) => {
                    const isThis = s.team_name === team.name;
                    return (
                      <tr key={i} className={`border-b border-slate-800/20 ${isThis ? 'bg-amber-500/10' : ''}`}>
                        <td className="px-4 py-2.5 text-slate-500">{s.rank || i+1}</td>
                        <td className="px-4 py-2.5">
                          <Link to={`${wcBase}/${teamPathSeg}/${_teamSlug(s.team_name)}`} className="flex items-center gap-2 hover:text-amber-300 transition-colors">
                            <img src={s.team_logo} alt="" className="w-5 h-5" />
                            <span className={isThis ? 'text-amber-400 font-semibold' : 'text-slate-300'}>{translateCountryName(s.team_name, currentLang)}</span>
                          </Link>
                        </td>
                        <td className="text-center px-2 py-2.5 text-slate-400">{s.played}</td>
                        <td className="text-center px-2 py-2.5 text-emerald-400">{s.won}</td>
                        <td className="text-center px-2 py-2.5 text-slate-400">{s.drawn}</td>
                        <td className="text-center px-2 py-2.5 text-rose-400">{s.lost}</td>
                        <td className="text-center px-2 py-2.5 font-bold text-white">{s.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>

            {/* Team's WC Matches */}
            <section className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50">
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                  <Calendar className="w-4 h-4 inline mr-2 text-amber-400" />{t('wc.teamMatchesTitle', { name: translateCountryName(team.name, currentLang) })}
                </h2>
              </div>
              <div className="divide-y divide-slate-800/30">
                {wc_matches.map(m => {
                  const date = m.date ? new Date(m.date) : null;
                  return (
                    <Link key={m.id} to={`${wcBase}/${matchPathSeg}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-800/30 transition-colors">
                      <div className="w-14 text-center shrink-0">
                        <p className="text-xs text-slate-400">{date?.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}</p>
                        <p className="text-[10px] text-slate-500">{date?.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</p>
                      </div>
                      <img src={m.home_logo} alt="" className="w-6 h-6" />
                      <span className={`text-sm flex-1 ${m.home_name === team.name ? 'text-amber-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.home_name, currentLang)}</span>
                      <span className="text-slate-600 text-xs">vs</span>
                      <span className={`text-sm flex-1 text-right ${m.away_name === team.name ? 'text-amber-400 font-semibold' : 'text-slate-300'}`}>{translateCountryName(m.away_name, currentLang)}</span>
                      <img src={m.away_logo} alt="" className="w-6 h-6" />
                      <span className="text-[10px] text-slate-500 w-28 text-right shrink-0">{m.venue_name}</span>
                    </Link>
                  );
                })}
              </div>
            </section>

            {/* All Group Matches */}
            <section className="bg-slate-900/60 border border-white/5 rounded-xl overflow-hidden">
              <div className="p-4 border-b border-slate-800/50">
                <h2 className="text-sm font-bold text-white uppercase tracking-wider">
                  <Calendar className="w-4 h-4 inline mr-2 text-emerald-400" />{t('wc.allGroupMatches', { group: translatedGroup })}
                </h2>
              </div>
              <div className="divide-y divide-slate-800/30">
                {all_group_matches.map(m => {
                  const date = m.date ? new Date(m.date) : null;
                  const involves = m.home_name === team.name || m.away_name === team.name;
                  return (
                    <Link key={m.id} to={`${wcBase}/${matchPathSeg}/${wcMatchSlug(m.home_name, m.away_name, m.id, currentLang)}`}
                      className={`flex items-center gap-3 px-4 py-2.5 hover:bg-slate-800/30 transition-colors ${involves ? 'bg-amber-500/5' : ''}`}>
                      <span className="text-[10px] text-slate-500 w-12 text-center shrink-0">{date?.toLocaleDateString(locale, { day: 'numeric', month: 'short' })}</span>
                      <img src={m.home_logo} alt="" className="w-4 h-4" />
                      <span className={`text-xs flex-1 ${m.home_name === team.name ? 'text-amber-400 font-medium' : 'text-slate-400'}`}>{translateCountryName(m.home_name, currentLang)}</span>
                      <span className="text-slate-600 text-[10px]">vs</span>
                      <span className={`text-xs flex-1 text-right ${m.away_name === team.name ? 'text-amber-400 font-medium' : 'text-slate-400'}`}>{translateCountryName(m.away_name, currentLang)}</span>
                      <img src={m.away_logo} alt="" className="w-4 h-4" />
                    </Link>
                  );
                })}
              </div>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-900/60 border border-white/5 rounded-xl p-4">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-3">
                <Swords className="w-4 h-4 inline mr-2 text-amber-400" />{t('wc.opponents')}
              </h3>
              {opponents.map((o, i) => (
                <Link key={i} to={`${wcBase}/${teamPathSeg}/${_teamSlug(o.name)}`}
                  className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800/30 mb-1 transition-colors">
                  <img src={o.logo} alt="" className="w-8 h-8" />
                  <span className="text-sm font-medium text-slate-300 hover:text-amber-300">{translateCountryName(o.name, currentLang)}</span>
                </Link>
              ))}
            </div>

            <div className="bg-gradient-to-br from-slate-800 to-slate-900 border border-amber-500/20 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-bold text-white">{t('wc.teamAnalysis')}</h3>
                <span className="text-[8px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">{t('wc.soonLabel')}</span>
              </div>
              <p className="text-xs text-slate-400">{t('wc.teamAnalysisDesc', { name: translateCountryName(team.name, currentLang) })}</p>
            </div>

            <Link to={`${wcBase}/${grpPathSeg}/${groupLetter}`} className="block bg-slate-800/50 border border-white/5 rounded-xl p-3 text-center text-sm text-amber-400 hover:text-amber-300 hover:bg-slate-800 transition-colors">
              {t('wc.groupDetail', { group: translatedGroup })}
            </Link>
            <Link to={wcBase} className="block bg-slate-800/50 border border-white/5 rounded-xl p-3 text-center text-sm text-slate-400 hover:text-slate-300 hover:bg-slate-800 transition-colors">
              {t('wc.wcHub')}
            </Link>
          </div>
        </div>

        <WCInternalLinks currentPage={`team-${slug}`} lang={currentLang} />
      </div>
    </div>
  );
}
